import { useEffect } from "react";

interface SeoJsonLd {
  [key: string]: unknown;
}

interface UseSeoOptions {
  title: string;
  description?: string;
  canonical?: string;
  /**
   * JSON-LD structured data (schema.org). Renders as a <script type="application/ld+json">.
   * Multiple objects supported.
   */
  jsonLd?: SeoJsonLd | SeoJsonLd[];
  /**
   * Yandex.Metrika hit tracking — hit on the current URL with the provided title.
   * Defaults to true.
   */
  trackHit?: boolean;
}

const JSON_LD_ATTR = "data-seo-jsonld";
const META_DESC_ATTR = "data-seo-description";
const CANONICAL_ATTR = "data-seo-canonical";

const upsertMeta = (name: string, content: string) => {
  if (typeof document === "undefined") return null;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[name="${name}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute(META_DESC_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertMetaProperty = (property: string, content: string) => {
  if (typeof document === "undefined") return null;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    el.setAttribute(META_DESC_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertCanonical = (href: string) => {
  if (typeof document === "undefined") return null;
  let el = document.head.querySelector<HTMLLinkElement>(
    `link[rel="canonical"]`
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute(CANONICAL_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const injectJsonLd = (payload: SeoJsonLd | SeoJsonLd[]) => {
  if (typeof document === "undefined") return () => undefined;
  const items = Array.isArray(payload) ? payload : [payload];
  const nodes: HTMLScriptElement[] = items.map((item) => {
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.setAttribute(JSON_LD_ATTR, "true");
    node.text = JSON.stringify(item);
    document.head.appendChild(node);
    return node;
  });
  return () => {
    nodes.forEach((node) => node.remove());
  };
};

export const useSeo = ({
  title,
  description,
  canonical,
  jsonLd,
  trackHit = true,
}: UseSeoOptions): void => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    document.title = title;

    const managedMeta: (HTMLMetaElement | null)[] = [];

    let descMeta: HTMLMetaElement | null = null;
    if (description) {
      descMeta = upsertMeta("description", description);
      managedMeta.push(
        upsertMetaProperty("og:title", title),
        upsertMetaProperty("og:description", description),
        upsertMetaProperty("og:type", "website")
      );
    }

    let canonicalLink: HTMLLinkElement | null = null;
    if (canonical) {
      const href =
        canonical.startsWith("http")
          ? canonical
          : `${window.location.origin}${canonical}`;
      canonicalLink = upsertCanonical(href);
      managedMeta.push(upsertMetaProperty("og:url", href));
    }

    // Если JSON-LD уже отрендерен на сервере (SSR), не добавляем дубликат на клиенте.
    const hasSsrJsonLd = !!document.head.querySelector("[data-ssr-jsonld]");
    const removeJsonLd =
      jsonLd && !hasSsrJsonLd ? injectJsonLd(jsonLd) : () => undefined;

    if (trackHit && typeof window !== "undefined" && window.ym) {
      try {
        window.ym(96258803, "hit", window.location.href, {
          title,
          referer: document.referrer,
        } as Record<string, unknown>);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.log("Yandex Metrika not available");
        }
      }
    }

    return () => {
      document.title = previousTitle;
      removeJsonLd();
      if (descMeta?.getAttribute(META_DESC_ATTR) === "true") descMeta.remove();
      managedMeta.forEach((el) => {
        if (el?.getAttribute(META_DESC_ATTR) === "true") el.remove();
      });
      if (canonicalLink?.getAttribute(CANONICAL_ATTR) === "true")
        canonicalLink.remove();
    };
  }, [title, description, canonical, jsonLd, trackHit]);
};

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ym?: (counterId: number, method: string, target: string, params?: any) => void;
  }
}
